import { OrderFreightState, OrderFreightType, OrderFreightTypeMap, OrderStopType } from "@/constants/order";
import type { OrderFreightDto } from "@/api/orders/dto/order.dto";
import type { TripRoute, TripRouteFreight } from "@/types/trip";
import { generateId } from "@/utils/generateId";
import { getEntityKey } from "@/utils/sameEntity";

export type FreightMismatchEntry = { freightNumber: string; diff: string };

export type FreightMismatchResult<T extends OrderFreightDto = OrderFreightDto> = {
  notAllocatedFreights: T[];
  mismatchFreights: FreightMismatchEntry[];
};

export function useFreights() {
  const freightTypePallets = "pl";
  const freightTypePieces = "pc";

  function getFreightName(
    freight: TripRouteFreight | OrderFreightDto | undefined,
    label: number | string | null = null,
    firstName = "Freight",
    withStackable = true,
    asPlanned = false,
  ): string {
    if (!freight) {
      return "";
    }

    const nameParts = [firstName, label ? `#${label}:` : ""];

    if (asPlanned) {
      if ("plannedQuantity" in freight && freight.plannedQuantity) {
        nameParts.push(freight.plannedQuantity.toString(), freight.type === "pl" ? "pallets" : "pieces");
      }
      if ("plannedWeight" in freight && freight.plannedWeight) {
        nameParts.push(freight.plannedWeight.toString(), "lbs");
      }
    } else {
      if (freight.quantity) {
        nameParts.push(freight.quantity.toString(), freight.type === "pl" ? "pallets" : "pieces");
      }
      if (freight.weight) {
        nameParts.push(freight.weight.toString(), "lbs");
      }
    }

    if (freight.length && freight.width && freight.height) {
      nameParts.push(`${freight.length}x${freight.width}x${freight.height}`);
    }

    if (!freight.stackable && withStackable) {
      nameParts.push("Not stackable");
    }

    return nameParts.join(" ").trim();
  }

  function getFreightLabel(freight: TripRouteFreight | OrderFreightDto | undefined): string {
    return freight?.name?.split(":")[0]?.trim() ?? "";
  }

  function getFreightTypeName(freight: OrderFreightDto) {
    return OrderFreightTypeMap.find((ft) => ft.id === freight.type)?.name;
  }

  function createOrderFreightFromRouteFreight(routeFreight: TripRouteFreight): OrderFreightDto {
    return {
      id: routeFreight.freightId,
      name: routeFreight.name,
      number: routeFreight.number,
      type: routeFreight.type,
      isDeleted: routeFreight.isDeleted,
      height: routeFreight.height,
      weight: routeFreight.plannedWeight || 0,
      length: routeFreight.length,
      quantity: routeFreight.plannedQuantity || 0,
      state: routeFreight.state,
      stackable: routeFreight.stackable,
    };
  }

  function createRouteFreightFromOrderFreight(
    orderFreight: OrderFreightDto,
    options: {
      index?: number;
      id?: string;
      quantity?: number | null;
      weight?: number | null;
    } = {},
  ): TripRouteFreight {
    const { index, id, quantity, weight } = options;

    return {
      id: id || generateId(),
      $existedUIOnly: !id,
      freightId: orderFreight.id!,
      type: orderFreight.type!,
      plannedWeight: weight || orderFreight.weight,
      plannedQuantity: quantity || orderFreight.quantity,
      length: orderFreight.length,
      width: orderFreight.width,
      height: orderFreight.height,
      stackable: orderFreight.stackable,
      number: (index || 0) + 1,
      isDeleted: orderFreight.isDeleted,
      $isNew: true,
      state: orderFreight.state || OrderFreightState.normal,
    };
  }

  function getFreightCharacteristics(freight: TripRouteFreight) {
    return {
      type: freight.type,
      plannedQuantity: Number(freight.plannedQuantity),
      plannedWeight: Number(freight.plannedWeight),
      quantity: Number(freight.quantity),
      weight: Number(freight.weight),
      length: freight.length,
      width: freight.width,
      height: freight.height,
      stackable: freight.stackable,
    };
  }

  function hasFreightChanged(initialFreight: TripRouteFreight, updatedFreight: TripRouteFreight): boolean {
    return (
      JSON.stringify(getFreightCharacteristics(initialFreight)) !==
      JSON.stringify(getFreightCharacteristics(updatedFreight))
    );
  }

  function createFreightState(
    updatedFreight: TripRouteFreight,
    initialFreight: TripRouteFreight | undefined,
  ): OrderFreightState {
    if (!initialFreight) {
      return updatedFreight.state;
    }

    return hasFreightChanged(initialFreight, updatedFreight) ? OrderFreightState.updated : updatedFreight.state;
  }

  function hasRouteFreightMismatch(routeFreights: TripRouteFreight[], orderFreights: OrderFreightDto[]): boolean {
    const orderFreightsById = new Map(orderFreights.map((freight) => [freight.id, freight]));

    const routeFreightsByOrderFreightId = routeFreights
      .filter((freight) => !freight.isDeleted)
      .reduce<Map<string, TripRouteFreight[]>>((acc, freight) => {
        const related = acc.get(freight.freightId) ?? [];
        related.push(freight);
        acc.set(freight.freightId, related);

        return acc;
      }, new Map());

    return [...routeFreightsByOrderFreightId.entries()].some(([orderFreightId, relatedRouteFreights]) => {
      const orderFreight = orderFreightsById.get(orderFreightId);

      if (!orderFreight || orderFreight.state !== OrderFreightState.updated) {
        return false;
      }

      const [routeFreight] = relatedRouteFreights;

      if (!routeFreight || routeFreight.state === OrderFreightState.updated) {
        return false;
      }

      const expectedFreight: TripRouteFreight = {
        ...createRouteFreightFromOrderFreight(orderFreight),
        weight: routeFreight.weight,
        quantity: routeFreight.quantity,
      };

      return hasFreightChanged(routeFreight, expectedFreight);
    });
  }

  function calculateTotalFreights(freights: OrderFreightDto[]): string {
    const totals = freights
      .filter((f) => !f.isDeleted)
      .reduce(
        (acc, f) => {
          if (f.type === OrderFreightType.pallets && f.quantity) {
            acc.plt += f.quantity;
          }
          if (f.type === OrderFreightType.pieces && f.quantity) {
            acc.pcs += f.quantity;
          }
          if (f.weight) {
            acc.lbs += f.weight;
          }
          return acc;
        },
        { plt: 0, pcs: 0, lbs: 0 },
      );

    const parts: string[] = [];
    if (totals.plt) {
      parts.push(`${totals.plt} plt`);
    }
    if (totals.pcs) {
      parts.push(`${totals.pcs} pcs`);
    }
    if (totals.lbs) {
      parts.push(`${totals.lbs} lbs`);
    }

    return parts.join(", ");
  }

  function getUnusedFreights(route: TripRoute): TripRouteFreight[] {
    return route.freights.filter((f) => {
      return !route.routeStops
        .filter((rs) => rs.type === OrderStopType.delivery && rs.use && !rs.isDeleted)
        .flatMap((s) => {
          return s.freights;
        })
        .includes(getEntityKey(f));
    });
  }

  function buildFreightMismatch(
    expected: { name?: string; type?: OrderFreightType; quantity?: number; weight?: number },
    related: TripRouteFreight[],
  ): FreightMismatchEntry | null {
    const allocatedQuantity = related.reduce((acc, f) => acc + Number(f.plannedQuantity), 0);
    const allocatedWeight = related.reduce((acc, f) => acc + Number(f.plannedWeight), 0);
    const expectedQuantity = expected.quantity ?? 0;
    const expectedWeight = expected.weight ?? 0;

    if (allocatedQuantity === expectedQuantity && allocatedWeight === expectedWeight) {
      return null;
    }

    const chunks: string[] = [];
    const quantityDiff = allocatedQuantity - expectedQuantity;
    const weightDiff = allocatedWeight - expectedWeight;

    if (quantityDiff > 0) chunks.push(`selected additional ${quantityDiff} ${expected.type}`);
    if (quantityDiff < 0) chunks.push(`not selected ${-quantityDiff} ${expected.type}`);
    if (weightDiff > 0) chunks.push(`selected additional ${weightDiff} lbs`);
    if (weightDiff < 0) chunks.push(`not selected ${-weightDiff} lbs`);

    if (chunks.length === 0) {
      return null;
    }

    return {
      freightNumber: expected.name ?? "",
      diff: chunks.join(", "),
    };
  }

  function getFreightMismatch<T extends OrderFreightDto>(
    exceptedFreights: T[],
    relatedFreights: TripRouteFreight[],
  ): FreightMismatchResult<T> {
    return exceptedFreights.reduce<FreightMismatchResult<T>>(
      (acc, freight) => {
        const related = relatedFreights.filter((rf) => rf.freightId === freight.id);

        if (related.length === 0) {
          acc.notAllocatedFreights.push(freight);
          return acc;
        }

        const mismatch = buildFreightMismatch(freight, related);

        if (mismatch) {
          acc.mismatchFreights.push(mismatch);
        }

        return acc;
      },
      { notAllocatedFreights: [], mismatchFreights: [] },
    );
  }

  return {
    freightTypePallets,
    freightTypePieces,
    getFreightName,
    getFreightLabel,
    getFreightTypeName,
    createOrderFreightFromRouteFreight,
    createRouteFreightFromOrderFreight,
    hasFreightChanged,
    createFreightState,
    hasRouteFreightMismatch,
    calculateTotalFreights,
    getUnusedFreights,
    getFreightMismatch,
  };
}
